import React from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { X } from 'lucide-react';
import { useSettings } from '../../../providers/settings-provider';

interface OccurrenceRow {
    date: string;
    amount: number;
    isActive?: boolean;
    linkedTransactionId?: string | null;
}

interface ProvisionOccurrencesPanelProps {
    occurrences: OccurrenceRow[];
    isLoading: boolean;
    onClose: () => void;
}

const getStatusLabel = (row: OccurrenceRow): string =>
    row.isActive === false ? 'Deleted' : row.linkedTransactionId ? 'Linked' : 'Unlinked';

const getStatusColor = (row: OccurrenceRow): string =>
    row.isActive === false ? 'var(--color-negative-amount)' : row.linkedTransactionId ? 'var(--color-primary)' : 'var(--color-text-muted)';

// Left-side panel shown inside the Edit Provision modal for recurring
// templates, listing every occurrence this template has generated (including
// soft-deleted ones, greyed out) so the user can see the schedule while
// editing it - reuses the same fetchTransactionsById data as the
// "Occurrences" tab in related-transactions-detail.tsx. A plain flex list
// rather than MaterialReactTable: MRT's built-in cell padding/column-resize
// chrome doesn't fit a ~280px sidebar - it forced both a horizontal
// scrollbar and a truncated "Amount" header.
const ProvisionOccurrencesPanel: React.FC<ProvisionOccurrencesPanelProps> = ({ occurrences, isLoading, onClose }) => {
    const { formatDate, formatAmount } = useSettings();
    const [sort, setSort] = React.useState<{ column: 'date' | 'status'; direction: 'asc' | 'desc' }>({ column: 'date', direction: 'asc' });

    const toggleSort = (column: 'date' | 'status') => {
        setSort(prev => ({ column, direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc' }));
    };

    const sorted = React.useMemo(() => {
        const dir = sort.direction === 'asc' ? 1 : -1;
        return [...occurrences].sort((a, b) => {
            if (sort.column === 'date') return (new Date(a.date).getTime() - new Date(b.date).getTime()) * dir;
            return getStatusLabel(a).localeCompare(getStatusLabel(b)) * dir;
        });
    }, [occurrences, sort]);

    const renderSortableHeader = (column: 'date' | 'status', label: string, align: 'left' | 'right') => (
        <Box
            onClick={() => toggleSort(column)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: 0.25, cursor: 'pointer', userSelect: 'none' }}
        >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)' }}>{label}</Typography>
            <Typography sx={{ fontSize: 10, opacity: sort.column === column ? 0.9 : 0.3, color: sort.column === column ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
                {sort.column === column ? (sort.direction === 'asc' ? '▲' : '▼') : '▼'}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0 }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1,
                flexShrink: 0, background: 'var(--color-background, #fff)',
            }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Occurrences</Typography>
                <IconButton size="small" onClick={onClose} aria-label="Hide occurrences">
                    <X size={16} />
                </IconButton>
            </Box>
            {isLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={24} />
                </Box>
            ) : sorted.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', py: 4, px: 1.5 }}>
                    No occurrences yet
                </Typography>
            ) : (
                <>
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderTop: '1px solid #e0e0e0',
                        borderBottom: '1px solid #e0e0e0',
                        background: 'var(--color-container-background)',
                        flexShrink: 0,
                    }}>
                        <Box sx={{ flex: '1 1 0' }}>{renderSortableHeader('date', 'Date', 'left')}</Box>
                        <Typography sx={{ flex: '1 1 0', fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', textAlign: 'right' }}>Amount</Typography>
                        <Box sx={{ flex: '0 0 60px' }}>{renderSortableHeader('status', 'Status', 'right')}</Box>
                    </Box>
                    {/* Only the row list scrolls - the title bar and column
                        header above stay fixed instead of scrolling away
                        with a full-panel scrollbar. */}
                    <Box sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: 5 },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(0, 0, 0, 0.2)', borderRadius: 3 },
                        scrollbarWidth: 'thin',
                    }}>
                        {sorted.map((row, i) => {
                            const deleted = row.isActive === false;
                            const label = getStatusLabel(row);
                            const statusColor = getStatusColor(row);
                            return (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 1.5,
                                        py: 0.65,
                                        borderBottom: '1px solid #f0f0f0',
                                        opacity: deleted ? 0.5 : 1,
                                    }}
                                >
                                    <Typography sx={{ flex: '1 1 0', fontSize: 12 }}>{formatDate(new Date(row.date))}</Typography>
                                    <Typography sx={{ flex: '1 1 0', fontSize: 12, textAlign: 'right', color: Number(row.amount) <= 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)' }}>
                                        {formatAmount?.(row.amount)}
                                    </Typography>
                                    <Typography sx={{ flex: '0 0 60px', fontSize: 11, fontWeight: 600, textAlign: 'right', color: statusColor }}>
                                        {label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default ProvisionOccurrencesPanel;
